import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { MapPin, MapTrifold, Warning, Truck as TruckIcon, ArrowRight } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useSites } from "@/lib/sites";
import { useAuth, filterSitesForUser, getUserAllowedSites } from "@/lib/auth";
import { useAutoRefresh } from "@/lib/useAutoRefresh";
import { toast } from "sonner";

const STATUS_META = {
  actif: { label: "Actif", bg: "#16A34A", emoji: "✓" },
  panne: { label: "En panne", bg: "#DC2626", emoji: "🚨" },
  red_tag: { label: "Red Tag", bg: "#991B1B", emoji: "🚩" },
  attente_expertise: { label: "Att. expert", bg: "#9333EA", emoji: "🔎" },
  inactif: { label: "Inactif", bg: "#6B7280", emoji: "—" },
  rendu: { label: "Rendu", bg: "#2563EB", emoji: "↩" },
};

function iconForTruck(t) {
  const meta = STATUS_META[t.status] || STATUS_META.inactif;
  return L.divIcon({
    html: `<div style="background:${meta.bg};color:#fff;border:2px solid #fff;border-radius:50%;width:36px;height:36px;display:flex;align-items:center;justify-content:center;font-weight:900;font-size:14px;box-shadow:0 4px 12px rgba(0,0,0,0.35);">${meta.emoji}</div>`,
    className: "",
    iconSize: [36, 36],
    iconAnchor: [18, 18],
  });
}

function FitBoundsToMarkers({ points }) {
  const map = useMap();
  useEffect(() => {
    if (points.length === 0) return;
    if (points.length === 1) {
      map.setView([points[0].lat, points[0].lng], 12);
      return;
    }
    const bounds = L.latLngBounds(points.map((p) => [p.lat, p.lng]));
    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 12 });
  }, [points, map]);
  return null;
}

export default function FleetMapPage() {
  const { sites: allSites } = useSites();
  const { user } = useAuth();
  const sites = filterSitesForUser(allSites, user);
  const userScope = getUserAllowedSites(user);
  const defaultTab = userScope && userScope.length === 1 ? userScope[0] : "ALL";
  const [siteTab, setSiteTab] = useState(defaultTab);
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [trucks, setTrucks] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = React.useCallback((opts = {}) => {
    const silent = opts.silent === true;
    return api
      .get(`/trucks?site=${siteTab}`)
      .then((r) => setTrucks(r.data || []))
      .catch(() => { if (!silent) toast.error("Erreur de chargement"); })
      .finally(() => { if (!silent) setLoading(false); });
  }, [siteTab]);

  useEffect(() => {
    setLoading(true);
    load();
  }, [load]);
  useAutoRefresh(() => load({ silent: true }), 20000);

  // Trucks with valid GPS
  // Exclude "rendu" trucks entirely from the fleet map — they're archived
  const activeTrucks = useMemo(() => trucks.filter((t) => t.status !== "rendu"), [trucks]);

  const localizedTrucks = useMemo(() => {
    return activeTrucks.filter(
      (t) =>
        typeof t.location_lat === "number" &&
        typeof t.location_lng === "number" &&
        (statusFilter === "ALL" || t.status === statusFilter)
    );
  }, [activeTrucks, statusFilter]);

  const unlocatedCount = activeTrucks.length - activeTrucks.filter(
    (t) => typeof t.location_lat === "number" && typeof t.location_lng === "number"
  ).length;

  const statusCounts = useMemo(() => {
    const counts = {};
    activeTrucks.forEach((t) => {
      counts[t.status] = (counts[t.status] || 0) + 1;
    });
    return counts;
  }, [activeTrucks]);

  const points = localizedTrucks.map((t) => ({ lat: t.location_lat, lng: t.location_lng, id: t.id }));

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-8 py-8 paper-bg min-h-screen" data-testid="fleet-map-page">
      {/* Hero */}
      <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="label-tag">Vue temps réel · Positions GPS</div>
          <h1 className="serif-display text-5xl md:text-6xl mt-2 leading-none">
            Carte de la flotte
          </h1>
          <div className="ny-divider w-40 mt-3" />
          <p className="text-zinc-700 mt-3 max-w-xl">
            Voir en un coup d&apos;œil où se trouvent les camions localisés, avec code couleur par statut.
          </p>
        </div>
      </div>

      {/* Site tabs */}
      <div className="flex gap-0 mb-4 flex-wrap" data-testid="fleet-map-site-tabs">
        {[
          ...sites.map((s) => ({ key: s.code, label: s.name ? `${s.code} · ${s.name}` : s.code })),
          ...(sites.length > 1 ? [{ key: "ALL", label: "Tous les sites" }] : []),
        ].map((s) => {
          const active = siteTab === s.key;
          return (
            <button
              key={s.key}
              onClick={() => setSiteTab(s.key)}
              className={`px-5 py-3 text-xs uppercase tracking-widest font-bold border border-zinc-900 inline-flex items-center gap-2 transition-none ${
                active ? "bg-zinc-900 text-white" : "bg-white text-zinc-900 hover:bg-zinc-100 border-b-2"
              }`}
              data-testid={`fleet-map-site-${s.key}`}
            >
              <MapPin size={14} weight="bold" /> {s.label}
            </button>
          );
        })}
      </div>

      {/* Status filter chips */}
      <div className="flex items-center gap-2 mb-4 flex-wrap" data-testid="fleet-map-status-filters">
        <button
          onClick={() => setStatusFilter("ALL")}
          className={`px-3 py-1.5 text-[11px] uppercase tracking-widest font-bold border ${
            statusFilter === "ALL"
              ? "bg-zinc-900 text-white border-zinc-900"
              : "bg-white text-zinc-900 border-zinc-300 hover:border-zinc-900"
          }`}
        >
          Tous · {activeTrucks.filter(t=>typeof t.location_lat==='number').length}
        </button>
        {Object.entries(STATUS_META).map(([key, meta]) => {
          if (key === "rendu") return null; // rendu trucks are excluded from map entirely
          const count = statusCounts[key] || 0;
          if (count === 0) return null;
          const active = statusFilter === key;
          return (
            <button
              key={key}
              onClick={() => setStatusFilter(key)}
              className={`px-3 py-1.5 text-[11px] uppercase tracking-widest font-bold border inline-flex items-center gap-2 ${
                active ? "text-white border-zinc-900" : "bg-white border-zinc-300 hover:border-zinc-900"
              }`}
              style={active ? { backgroundColor: meta.bg } : {}}
              data-testid={`fleet-map-filter-${key}`}
            >
              <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: meta.bg }} />
              {meta.label} · {count}
            </button>
          );
        })}
      </div>

      {/* Alert if some trucks not localized */}
      {unlocatedCount > 0 && (
        <div className="border-2 border-amber-500 bg-amber-50 px-4 py-3 mb-4 flex items-center gap-3" data-testid="fleet-map-warning-unlocated">
          <Warning size={20} weight="fill" className="text-amber-700 flex-shrink-0" />
          <div className="text-sm text-amber-900 flex-1">
            <b>{unlocatedCount}</b> camion(s) sans position enregistrée ne sont pas affichés sur la carte.
            Ouvrez leur fiche et renseignez la position pour les faire apparaître ici.
          </div>
        </div>
      )}

      {/* Map or empty state */}
      {loading ? (
        <div className="border border-zinc-900 bg-white p-12 text-center text-sm uppercase tracking-widest text-zinc-500">
          Chargement…
        </div>
      ) : localizedTrucks.length === 0 ? (
        <div className="border-2 border-dashed border-zinc-300 bg-zinc-50 p-12 text-center" data-testid="fleet-map-empty">
          <MapTrifold size={48} weight="duotone" className="mx-auto text-zinc-400 mb-3" />
          <div className="text-lg font-bold text-zinc-700 mb-2">Aucun camion localisé sur la carte</div>
          <div className="text-sm text-zinc-500 max-w-md mx-auto">
            Ouvrez la fiche d&apos;un camion, cliquez sur <b>+ Ajouter position</b> et tapez son adresse.
            Il apparaîtra ici automatiquement.
          </div>
        </div>
      ) : (
        <div className="border-2 border-zinc-900 relative" data-testid="fleet-map-container">
          <div className="h-[600px]">
            <MapContainer
              center={[46.6, 2.5]}
              zoom={6}
              style={{ height: "100%", width: "100%" }}
              scrollWheelZoom
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <FitBoundsToMarkers points={points} />
              {localizedTrucks.map((t) => {
                const meta = STATUS_META[t.status] || STATUS_META.inactif;
                return (
                  <Marker
                    key={t.id}
                    position={[t.location_lat, t.location_lng]}
                    icon={iconForTruck(t)}
                  >
                    <Popup>
                      <div className="text-xs min-w-[220px]">
                        <div className="flex items-center gap-2 mb-2">
                          <TruckIcon size={16} weight="bold" />
                          <b className="text-base">{t.license_plate}</b>
                        </div>
                        <div className="mb-1">
                          <span
                            className="px-2 py-0.5 font-bold text-[10px] uppercase tracking-widest"
                            style={{ background: meta.bg, color: "#fff" }}
                          >
                            {meta.label}
                          </span>
                          {t.status === "panne" && t.panne_severity && (
                            <span className="ml-2 text-[10px] uppercase tracking-widest text-zinc-500">
                              {t.panne_severity}
                            </span>
                          )}
                        </div>
                        {t.status === "panne" && t.panne_reason && (
                          <div className="text-red-700 italic my-1">
                            🚨 {t.panne_reason}
                          </div>
                        )}
                        {t.location_address && (
                          <div className="text-zinc-600 mt-1">{t.location_address}</div>
                        )}
                        {t.location_note && (
                          <div className="text-zinc-500 italic mt-1">📝 {t.location_note}</div>
                        )}
                        <Link
                          to={`/trucks/${t.id}`}
                          className="inline-flex items-center gap-1 mt-2 px-2 py-1 bg-zinc-900 text-white text-[10px] uppercase tracking-widest font-bold hover:bg-zinc-700"
                          data-testid={`fleet-map-popup-link-${t.id}`}
                        >
                          Ouvrir la fiche
                          <ArrowRight size={10} weight="bold" />
                        </Link>
                      </div>
                    </Popup>
                  </Marker>
                );
              })}
            </MapContainer>
          </div>
          <div className="px-4 py-2 border-t border-zinc-900 bg-zinc-50 text-[11px] uppercase tracking-widest text-zinc-500 flex items-center justify-between flex-wrap gap-2">
            <span>{localizedTrucks.length} camion(s) affiché(s) sur la carte</span>
            <span className="text-zinc-400">Clique un pin pour ouvrir la fiche du camion</span>
          </div>
        </div>
      )}
    </div>
  );
}
